<script setup>
import { computed } from 'vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useI18n } from 'vue-i18n';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';
import { useMapGetter } from 'dashboard/composables/store';

const { content, createdAt, inboxId } = useMessageContext();
const { t } = useI18n();

const readableTime = computed(() =>
  messageTimestamp(createdAt.value, 'LLL d, h:mm a')
);

// Try to parse content as tool call JSON
const toolCallData = computed(() => {
  if (!content.value) return null;

  try {
    const parsed = JSON.parse(content.value);
    // Check if it's a tool call (has tool, args  fields)
    if (parsed.tool && parsed.args) {
      return parsed;
    }
  } catch (e) {
    // Not JSON or invalid JSON, return null
  }
  return null;
});

// Convert tool name to readable format
// e.g., "update_contact_attributes_93ac87c225911e31d8cd7d3cadd54537" -> "Update Contact Attributes"
const readableToolName = computed(() => {
  if (!toolCallData.value) return '';

  const toolName = toolCallData.value.tool;
  // Remove UUID-like suffix (anything after underscore followed by 32+ hex chars)
  const nameWithoutId = toolName.replace(/_[a-f0-9]{32,}$/i, '');
  // Convert snake_case to Title Case
  return nameWithoutId
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
});

// Format args for display
const formattedArgs = computed(() => {
  if (!toolCallData.value || !toolCallData.value.args) return '';

  const args = toolCallData.value.args;
  const argPairs = Object.entries(args).map(([key, value]) => {
    // Format the value - if it's a string, show it as-is, otherwise JSON.stringify
    const formattedValue =
      typeof value === 'string' ? value : JSON.stringify(value);
    return `${key}: "${formattedValue}"`;
  });

  return argPairs.join(', ');
});

// Get the assistant associated with the inbox
const getAssistantByInboxId = useMapGetter('assistants/getAssistantByInboxId');

const assistant = computed(() => {
  return getAssistantByInboxId.value(inboxId.value);
});

// Get assistant name from the assistant associated with the inbox
const assistantName = computed(() => {
  return assistant.value?.name || t('ACTIVITY.DEFAULT_ASSISTANT_NAME');
});

// Formatted message for tool calls
const formattedMessage = computed(() => {
  if (!toolCallData.value) {
    return content.value;
  }

  if (formattedArgs.value) {
    return t('ACTIVITY.TOOL_EXECUTION_WITH_ARGS', {
      assistantName: `<strong>${assistantName.value}</strong>`,
      toolName: `<strong>${readableToolName.value}</strong>`,
      args: formattedArgs.value,
    });
  }

  return t('ACTIVITY.TOOL_EXECUTION', {
    assistantName: `<strong>${assistantName.value}</strong>`,
    toolName: `<strong>${readableToolName.value}</strong>`,
  });
});
</script>

<template>
  <BaseBubble
    v-tooltip.top="readableTime"
    class="px-3 py-1 !rounded-xl flex min-w-0 items-center gap-2"
    data-bubble-name="activity"
  >
    <span
      v-dompurify-html="formattedMessage"
      :title="content"
      class="break-all"
    />
  </BaseBubble>
</template>
